import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Bike, Users, ShoppingCart, Percent, ClipboardList, MessagesSquare, Wallet,
  Truck, ChartColumn, UserCog, MessageCircle, Megaphone, Search, Bell, ChevronRight, ChevronDown, LogOut, Globe, Menu, CirclePlay,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isVisualizacao, podeVerModulo, moduloDoPath } from '../utils/permissoes';
import ImpersonationBar, { impersonando, ALTURA_BARRA } from './ImpersonationBar';

// Paleta do redesign (Fase 1 — esqueleto)
const COR = {
  sidebar: '#0B1628',
  ativo: '#1A6DFF',
  fundo: '#E8ECF1',
  texto: '#1C2533',
  textoSec: '#6B7A8D',
  verde: '#2D7D4F',
  menu: 'rgba(255,255,255,0.72)',
  menuSec: 'rgba(255,255,255,0.45)',
  borda: 'rgba(255,255,255,0.08)',
};
// Link externo fixo no fim do menu — todos os níveis e empresas.
const URL_TUTORIAIS = 'https://youtube.com/@automaissistema?si=khO7E-KA-PKUpdjj';
const FONTE = "'DM Sans',system-ui,sans-serif";
const LARGURA_SIDEBAR = 220;
const ALTURA_TOPBAR = 56;

// Starter: gestão essencial (contrato e garantia em PDF disponíveis em todos os planos)
const menuStarter = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/estoque', label: 'Estoque' },
  { path: '/clientes', label: 'Clientes' },
  { path: '/vendas', label: 'Vendas' },
  { path: '/comissao', label: 'Comissão' },
  { path: '/relatorios', label: 'Relatórios' },
  { path: '/usuarios', label: 'Usuários' },
];

// Pro: tudo do Starter + Financeiro, CRM, Fornecedores
const menuPro = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/estoque', label: 'Estoque' },
  { path: '/clientes', label: 'Clientes' },
  { path: '/vendas', label: 'Vendas' },
  { path: '/comissao', label: 'Comissão' },
  { path: '/crm', label: 'CRM' },
  { path: '/financeiro', label: 'Financeiro' },
  { path: '/fornecedores', label: 'Fornecedores' },
  { path: '/relatorios', label: 'Relatórios' },
  { path: '/usuarios', label: 'Usuários' },
];

// Master: tudo do Pro (acesso completo)
const menuMaster = menuPro;

// Resolve o menu admin de acordo com o plano (mapeia nomes legados)
function menuDoPlano(plano) {
  const p = plano || 'starter';
  if (p === 'master' || p === 'completo') return menuMaster;
  if (p === 'pro' || p === 'profissional') return menuPro;
  return menuStarter; // starter / basico
}

// Páginas que o vendedor pode acessar (intersecção com o menu do plano)
const PATHS_VENDEDOR = ['/dashboard', '/estoque', '/clientes', '/vendas', '/comissao', '/crm'];

// Consórcio: exclusivo da Autop (empresa 12), liberado para TODOS os níveis.
// Entra logo depois de "Comissão" — por isso é inserido por posição, não no fim.
const ITEM_CONSORCIO = { path: '/consorcios', label: 'Consórcio' };
function inserirConsorcio(itens) {
  const i = itens.findIndex(m => m.path === '/comissao');
  const pos = i === -1 ? itens.length : i + 1;
  return [...itens.slice(0, pos), ITEM_CONSORCIO, ...itens.slice(pos)];
}

// Visual: ícone por rota e submenus (só com rotas que já existem).
const ICONES = {
  '/dashboard': LayoutDashboard, '/estoque': Bike, '/clientes': Users, '/vendas': ShoppingCart,
  '/comissao': Percent, '/consorcios': ClipboardList, '/crm': MessagesSquare, '/financeiro': Wallet,
  '/fornecedores': Truck, '/relatorios': ChartColumn, '/usuarios': UserCog, '/leads-whatsapp': MessageCircle, '/remarketing': Megaphone,
};
const SUBMENUS = {
  '/vendas': [
    { path: '/vendas', label: 'Todas as vendas', exato: true },
    { path: '/vendas/nova', label: 'Nova venda', exato: true },
  ],
};

const rotaAtiva = (pathname, path, exato) =>
  exato ? pathname === path : (pathname === path || pathname.startsWith(path + '/'));
const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Sininho: ainda só visual — o badge aparece quando houver notificações de verdade.
const NOTIFICACOES = 0;

// Aviso de vencimento do plano: aparece a partir de 2 dias antes e some quando o pagamento
// é confirmado (dias vem de /usuarios/permissoes/me a cada carga do app).
function BannerVencimento({ dias }) {
  if (dias === null || dias === undefined || dias > 2) return null;
  const texto = dias > 0 ? `Seu plano vence em ${dias} dia(s).`
    : dias === 0 ? 'Seu plano vence hoje.'
    : `Seu plano está vencido há ${-dias} dia(s).`;
  return (
    <div style={{ background: dias < 0 ? '#FDECEA' : '#FFF4E5', color: dias < 0 ? '#C0392B' : '#8A4B00',
      borderBottom: `1px solid ${dias < 0 ? '#F5C2BC' : '#FFD8A8'}`, padding: '10px 24px', fontSize: '13px', fontWeight: '600' }}>
      ⚠️ {texto} Regularize para evitar suspensão.
    </div>
  );
}

export default function Layout({ children }) {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuAberto, setMenuAberto] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [abertos, setAbertos] = useState({});
  const [busca, setBusca] = useState('');
  const [buscaFoco, setBuscaFoco] = useState(false);

  // O index.css (que zera a margem do body) não é importado: sem isto a margem padrão de 8px
  // do navegador descola a topbar da sidebar. Só enquanto o layout logado estiver montado.
  useEffect(() => {
    const anterior = document.body.style.margin;
    document.body.style.margin = '0';
    return () => { document.body.style.margin = anterior; };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente';
  const somenteLeitura = isVisualizacao(usuario);
  const plano = usuario?.plano || 'starter';
  const menuPlano = menuDoPlano(plano);
  const menuNivel = isAdmin ? menuPlano : menuPlano.filter(m => PATHS_VENDEDOR.includes(m.path));
  const isAutop = usuario?.empresa_id === 12;
  // Consórcio: só a Autop, mas para todos os níveis (vendedor/gerente/admin).
  const menuBase = isAutop ? inserirConsorcio(menuNivel) : menuNivel;
  // Leads WhatsApp: exclusivo da Autop (empresa 12), apenas admin/gerente.
  // Visualização: menu do plano (+ Consórcio na Autop) filtrado pelos módulos liberados.
  const menu = somenteLeitura
    ? (isAutop ? inserirConsorcio(menuPlano) : menuPlano).filter(m => podeVerModulo(usuario, moduloDoPath(m.path)))
    : (isAdmin && isAutop)
      ? [...menuBase, { path: '/leads-whatsapp', label: 'Leads WhatsApp' }, { path: '/remarketing', label: 'Remarketing' }]
      : menuBase;
  // Visualização não tem "Nova venda": sem submenus.
  const submenus = somenteLeitura ? {} : SUBMENUS;

  const iniciais = usuario?.nome?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U';
  const primeiroNome = usuario?.nome?.split(' ')[0] || 'Usuário';

  // Impersonação: a barra é fixa no topo, então sidebar e topbar descem na mesma medida
  // (senão o rodapé da sidebar — botão Sair — cai fora da viewport).
  const impBar = impersonando();
  const offset = impBar ? ALTURA_BARRA : 0;

  const ir = (path) => { navigate(path); if (isMobile) setMenuAberto(false); };

  // Busca: filtra os itens do menu (inclui submenus e o perfil) — sem backend.
  const destinos = [
    ...menu.flatMap(m => submenus[m.path]
      ? submenus[m.path].map(s => ({ path: s.path, label: `${m.label} › ${s.label}` }))
      : [{ path: m.path, label: m.label }]),
    { path: '/perfil', label: 'Meu perfil' },
  ];
  const resultados = busca.trim() ? destinos.filter(d => norm(d.label).includes(norm(busca.trim()))).slice(0, 8) : [];
  const escolher = (d) => { setBusca(''); setBuscaFoco(false); ir(d.path); };

  const itemEstilo = (ativo) => ({
    width: '100%', padding: '9px 12px', margin: '1px 0', background: ativo ? COR.ativo : 'transparent',
    border: 'none', borderRadius: '8px', color: ativo ? '#fff' : COR.menu, textAlign: 'left', cursor: 'pointer',
    fontSize: '13.5px', fontWeight: ativo ? '600' : '500', fontFamily: FONTE, display: 'flex', alignItems: 'center', gap: '10px',
  });

  const sidebar = (
    <div style={{
      width: LARGURA_SIDEBAR + 'px', background: COR.sidebar, display: 'flex', flexDirection: 'column',
      position: 'fixed', top: offset, left: isMobile ? (menuAberto ? 0 : -LARGURA_SIDEBAR - 20) : 0,
      height: `calc(100vh - ${offset}px)`, zIndex: 100, transition: 'left 0.25s ease', boxSizing: 'border-box',
    }}>
      <div style={{ padding: '18px 16px 16px', borderBottom: `1px solid ${COR.borda}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '36px', height: '36px', background: COR.verde, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', color: '#fff', flexShrink: 0 }}>A+</div>
        <div>
          <div style={{ fontSize: '17px', fontWeight: '700', color: '#fff', letterSpacing: '-0.3px', lineHeight: 1.1 }}>AutoMais</div>
          <div style={{ color: COR.menuSec, fontSize: '11px', marginTop: '2px' }}>Sistema de Gestão</div>
        </div>
      </div>

      <nav style={{ flex: 1, padding: '10px 10px', overflowY: 'auto' }}>
        {menu.map(item => {
          const Icone = ICONES[item.path] || LayoutDashboard;
          const filhos = submenus[item.path];
          const dentro = rotaAtiva(location.pathname, item.path, false);
          if (!filhos) {
            return (
              <button key={item.path} onClick={() => ir(item.path)} style={itemEstilo(dentro)}>
                <Icone size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{item.label}</span>
              </button>
            );
          }
          const aberto = abertos[item.path] ?? dentro;
          const Seta = aberto ? ChevronDown : ChevronRight;
          return (
            <div key={item.path}>
              <button onClick={() => setAbertos(a => ({ ...a, [item.path]: !aberto }))}
                style={{ ...itemEstilo(false), color: dentro ? '#fff' : COR.menu }}>
                <Icone size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{item.label}</span>
                <Seta size={15} color="#fff" style={{ flexShrink: 0, opacity: 0.8 }} />
              </button>
              {aberto && (
                <div style={{ margin: '2px 0 4px 20px', paddingLeft: '10px', borderLeft: `1px solid ${COR.borda}` }}>
                  {filhos.map(s => (
                    <button key={s.path} onClick={() => ir(s.path)}
                      style={{ ...itemEstilo(rotaAtiva(location.pathname, s.path, s.exato)), padding: '7px 10px', fontSize: '13px' }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <a href={URL_TUTORIAIS} target="_blank" rel="noreferrer"
          style={{ ...itemEstilo(false), color: COR.menuSec, textDecoration: 'none', boxSizing: 'border-box', marginTop: '6px' }}>
          <CirclePlay size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>Tutoriais</span>
        </a>
      </nav>

      <div style={{ padding: '12px 14px 14px', borderTop: `1px solid ${COR.borda}` }}>
        <button onClick={() => ir('/perfil')} title="Meu perfil"
          style={{ width: '100%', background: location.pathname === '/perfil' ? 'rgba(255,255,255,0.08)' : 'transparent', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: COR.ativo, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{iniciais}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: FONTE }}>{usuario?.nome || 'Usuário'}</div>
            <div style={{ color: COR.menuSec, fontSize: '11px', fontFamily: FONTE }}>{usuario?.nivel === 'visualizacao' ? 'visualização' : usuario?.nivel}</div>
          </div>
        </button>
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          <a href={usuario?.empresa_id === 12 ? "https://autopmotos.com" : "https://sales.automais.pro"} target="_blank" rel="noreferrer"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${COR.borda}`, color: COR.menu, padding: '6px 8px', borderRadius: '6px', fontSize: '11px', fontFamily: FONTE, textDecoration: 'none' }}>
            <Globe size={13} /> Ver site
          </a>
          <button onClick={logout}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'transparent', border: `1px solid ${COR.borda}`, color: COR.menuSec, padding: '6px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: FONTE }}>
            <LogOut size={13} /> Sair
          </button>
        </div>
      </div>
    </div>
  );

  const topbar = (
    <div style={{
      background: COR.sidebar, height: ALTURA_TOPBAR + 'px', display: 'flex', alignItems: 'center', gap: '14px',
      padding: isMobile ? '0 12px' : '0 24px', position: 'sticky', top: offset, zIndex: 50,
      borderBottom: `1px solid ${COR.borda}`, boxSizing: 'border-box',
    }}>
      {isMobile && (
        <button onClick={() => setMenuAberto(!menuAberto)} aria-label="Abrir menu"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#fff', display: 'flex' }}>
          <Menu size={22} />
        </button>
      )}
      <div style={{ position: 'relative', flex: 1, maxWidth: '380px' }}>
        <Search size={15} color={COR.menuSec} style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)' }} />
        <input value={busca} placeholder={isMobile ? "Buscar..." : "Buscar no sistema..."}
          onChange={e => setBusca(e.target.value)}
          onFocus={() => setBuscaFoco(true)}
          onBlur={() => setTimeout(() => setBuscaFoco(false), 150)}
          onKeyDown={e => {
            if (e.key === 'Enter' && resultados[0]) escolher(resultados[0]);
            if (e.key === 'Escape') { setBusca(''); e.currentTarget.blur(); }
          }}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px 8px 33px', background: 'rgba(255,255,255,0.07)', border: `1px solid ${COR.borda}`, borderRadius: '8px', color: '#fff', fontSize: '13px', fontFamily: FONTE, outline: 'none' }} />
        {buscaFoco && busca.trim() && (
          <div style={{ position: 'absolute', top: '42px', left: 0, right: 0, background: '#fff', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,25,40,0.18)', padding: '6px', zIndex: 60 }}>
            {resultados.length === 0
              ? <div style={{ padding: '8px 10px', fontSize: '13px', color: COR.textoSec, fontFamily: FONTE }}>Nada encontrado no menu</div>
              : resultados.map((d, i) => (
                <button key={d.path + d.label} onMouseDown={() => escolher(d)}
                  style={{ width: '100%', textAlign: 'left', padding: '8px 10px', background: i === 0 ? COR.fundo : 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: COR.texto, fontFamily: FONTE }}>
                  {d.label}
                </button>
              ))}
          </div>
        )}
      </div>
      <div style={{ flex: 1 }} />
      {!isMobile && (
        <span style={{ fontSize: '12.5px', color: COR.menuSec, fontFamily: FONTE, whiteSpace: 'nowrap' }}>
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      )}
      <button aria-label="Notificações" title="Notificações"
        style={{ position: 'relative', background: 'rgba(255,255,255,0.07)', border: `1px solid ${COR.borda}`, borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COR.menu, cursor: 'default', flexShrink: 0 }}>
        <Bell size={17} />
        {NOTIFICACOES > 0 && (
          <span style={{ position: 'absolute', top: '-4px', right: '-4px', minWidth: '17px', height: '17px', borderRadius: '9px', background: '#E74C3C', color: '#fff', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', boxSizing: 'border-box' }}>{NOTIFICACOES}</span>
        )}
      </button>
      <button onClick={() => ir('/perfil')} title="Meu perfil"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: COR.ativo, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#fff' }}>{iniciais}</div>
        {!isMobile && <span style={{ color: '#fff', fontSize: '13px', fontWeight: '500', fontFamily: FONTE }}>{primeiroNome}</span>}
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', paddingTop: offset, background: COR.fundo, fontFamily: FONTE, boxSizing: 'border-box' }}>
      <ImpersonationBar />
      {/* Overlay mobile */}
      {isMobile && menuAberto && (
        <div onClick={() => setMenuAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      )}
      {sidebar}
      <div style={{ marginLeft: isMobile ? 0 : LARGURA_SIDEBAR, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: `calc(100vh - ${offset}px)` }}>
        {topbar}
        <BannerVencimento dias={usuario?.assinatura?.dias_para_vencer} />
        <div style={{ flex: 1, padding: isMobile ? '16px' : '24px', overflowX: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}
